import { AssistChip, Box, ChipGroup } from '@zillow/constellation-10';
import { IconMessageFilled, IconProfileFilled } from '@zillow/constellation-icons';
import { useContext, useEffect, useMemo } from 'react';
import { useMessageListActions_GetConversationDataQuery } from '../../types/GraphqlTypes';
import { ModalType } from '../../types/ModalType';
import {
  emitDisplayRequestRenterProfileButtonEvent,
  emitRequestRenterProfileClickEvent,
  REQUEST_RENTER_PROFILE_CLICK_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/requestRenterProfile';
import type { TriggerLocation } from '../../constants/clickstreamEvents/globals';
import {
  CHOOSE_SAVED_REPLY_CLICK_EVENT_TYPE_ID,
  emitChooseSavedReplyClickEvent,
} from '../../constants/clickstreamEvents/savedReplyModal';
import StringConstants from '../../constants/StringConstants';
import useModal from '../../hooks/useModal';
import { AppContext } from '../../contexts/AppContextProvider';
import RequestRenterProfileModal from '../Modals/RequestRenterProfileModal';
import SavedReplyModal from '../Modals/SavedReplyModal';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import useConversationActions from '../../hooks/useConversationActions';
import { useAuth } from '@zg-rentals/auth-comet';
import { compactContainerStyles, defaultContainerStyles } from './styles';

type MessageListActionsProps = {
  conversationId: string;
  listingAlias: string;
  clientTimeZone: string;
  variant?: 'default' | 'compact';
  triggerLocation: TriggerLocation;
};

export default function MessageListActions({
  conversationId,
  listingAlias,
  clientTimeZone,
  variant = 'default',
  triggerLocation,
}: MessageListActionsProps): JSX.Element {
  const isCompact = variant === 'compact';
  const { isImpersonator } = useContext(AppContext);
  const { activeModal, setActiveModal } = useModal();
  const { user } = useAuth();
  const { hasErrorOnRequestRenterProfile, requestRenterProfile } = useConversationActions();

  const { data: conversationData } = useMessageListActions_GetConversationDataQuery({
    variables: {
      clientTimeZone,
      conversationId: conversationId!,
      listingAlias: listingAlias!,
    },
    fetchPolicy: 'cache-only',
  });

  const { inquiry, isConversationBlockedByZillow, referenceName, renterProfile, status } =
    conversationData?.ZRM_getConversationById || {};
  const { shouldShowRequestRenterProfileCta } = renterProfile || {};
  const { isPhoneLead, name } = inquiry || {};
  const { isSpam } = status || {};
  const isChooseSavedReplyDisabled = Boolean(isImpersonator || isPhoneLead || isSpam || isConversationBlockedByZillow);

  useEffect(() => {
    if (shouldShowRequestRenterProfileCta) {
      emitDisplayRequestRenterProfileButtonEvent(user, triggerLocation);
    }
  }, [shouldShowRequestRenterProfileCta, user, triggerLocation]);

  const chooseSavedReplyTooltipText = useMemo(() => {
    if (isChooseSavedReplyDisabled) {
      if (isPhoneLead) {
        return StringConstants.TOOLTIP.PHONE_LEAD;
      }
      if (isImpersonator) {
        return StringConstants.TOOLTIP.IMPERSONATION;
      }
      if (isSpam) {
        return StringConstants.TOOLTIP.REPORTED;
      }
      if (isConversationBlockedByZillow) {
        return StringConstants.TOOLTIP.BLOCKED_BY_ZILLOW;
      }
    }

    return '';
  }, [isChooseSavedReplyDisabled, isImpersonator, isPhoneLead, isSpam, isConversationBlockedByZillow]);

  const handleShowSavedReplyModal = (): void => {
    setActiveModal(ModalType.SavedReplyModal);
    emitChooseSavedReplyClickEvent(user, triggerLocation);
  };

  const handleShowRequestRenterProfileModal = (): void => {
    setActiveModal(ModalType.RequestRenterProfileModal);
    emitRequestRenterProfileClickEvent(user, triggerLocation);
  };

  return (
    <>
      {activeModal === ModalType.SavedReplyModal && <SavedReplyModal triggerLocation={triggerLocation} />}

      {activeModal === ModalType.RequestRenterProfileModal && (
        <RequestRenterProfileModal
          name={name || referenceName || ''}
          onRequestRenterProfileClick={() => requestRenterProfile({ conversationId, listingAlias, clientTimeZone })}
          hasError={hasErrorOnRequestRenterProfile}
        />
      )}

      <Box css={isCompact ? compactContainerStyles : defaultContainerStyles}>
        <ChipGroup
          aria-label="Message actions"
          css={{
            flexWrap: 'nowrap',
            '& > *': { flexShrink: 0 },
          }}
        >
          <TooltipWrapper
            tooltipPlacement="top"
            wrapInTooltip={isChooseSavedReplyDisabled}
            tooltipText={chooseSavedReplyTooltipText}
          >
            <AssistChip
              data-eventid={CHOOSE_SAVED_REPLY_CLICK_EVENT_TYPE_ID}
              aria-haspopup="true"
              aria-label="View saved reply messages"
              disabled={isChooseSavedReplyDisabled}
              onClick={handleShowSavedReplyModal}
              icon={<IconMessageFilled />}
            >
              Choose saved reply
            </AssistChip>
          </TooltipWrapper>

          {shouldShowRequestRenterProfileCta && (
            <TooltipWrapper
              tooltipPlacement="top"
              wrapInTooltip={isImpersonator}
              tooltipText={StringConstants.TOOLTIP.IMPERSONATION}
            >
              <AssistChip
                data-eventid={REQUEST_RENTER_PROFILE_CLICK_EVENT_TYPE_ID}
                aria-haspopup="true"
                aria-label="Request more information"
                disabled={isImpersonator}
                onClick={handleShowRequestRenterProfileModal}
                icon={<IconProfileFilled />}
              >
                Request more information
              </AssistChip>
            </TooltipWrapper>
          )}
        </ChipGroup>
      </Box>
    </>
  );
}
